import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  type GeneralBarDatum,
  type TotalsAnnotationsValues,
} from "@jana-technology/platform-ui";

import {
  SystemOverviewTotalChartsDocument,
  SystemOverviewTotalChartsQuery,
  SystemOverviewTotalChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  appendORIRData,
  appendSystemOverviewTooltipExtras,
  appendSystemOverviewSubgroups,
  makeBaseORIRDatum,
  makeORTabData,
} from "./utils";
import { usePublishedRiskUnitFormatter } from "../usePublishedRiskUnitFormatter";

export const useSystemOverviewTotalCharts = (
  options?: useQuery.Options<
    SystemOverviewTotalChartsQuery,
    SystemOverviewTotalChartsQueryVariables
  >,
) => {
  const result = useQuery(SystemOverviewTotalChartsDocument, options);
  const riskUnitFormatter = usePublishedRiskUnitFormatter();

  const series = useMemo(() => {
    const threatORTab: Array<GeneralBarDatum> = [];
    const threatIRTab: Array<GeneralBarDatum> = [];
    const consequenceORTab: Array<GeneralBarDatum> = [];
    const consequenceIRTab: Array<GeneralBarDatum> = [];
    const outcomeORTab: Array<GeneralBarDatum> = [];
    const outcomeIRTab: Array<GeneralBarDatum> = [];

    let threatORDataKeys: Array<string> = [];
    const threatIRDataKeys: Array<string> = [];
    const consequenceORDataKeys: Array<string> = [];
    const consequenceIRDataKeys: Array<string> = [];
    const outcomeORDataKeys: Array<string> = [];
    const outcomeIRDataKeys: Array<string> = [];

    const totals: TotalsAnnotationsValues = [];

    let yMaxStackVal;

    if (result.data) {
      const { systemOverviewTotalCharts } = result.data;

      for (let i = 0; i < systemOverviewTotalCharts.length; i++) {
        const chart = systemOverviewTotalCharts[i];
        const xBand = chart.fieldName ?? `Field${i + 1}`;

        const [threatORDatum, threatIRDatum] = makeBaseORIRDatum(xBand);

        const [consequenceORDatum, consequenceIRDatum] =
          makeBaseORIRDatum(xBand);

        const [outcomeORDatum, outcomeIRDatum] = makeBaseORIRDatum(xBand);

        threatORDatum.formatter = riskUnitFormatter;
        threatIRDatum.formatter = riskUnitFormatter;
        consequenceORDatum.formatter = riskUnitFormatter;
        consequenceIRDatum.formatter = riskUnitFormatter;
        outcomeORDatum.formatter = riskUnitFormatter;
        outcomeIRDatum.formatter = riskUnitFormatter;

        appendORIRData(
          chart.itemsByThreat,
          threatORDatum,
          threatORDataKeys,
          threatIRDatum,
          threatIRDataKeys,
        );

        threatORDataKeys = threatORDataKeys.filter(
          (key) => !key.includes("Intervention"),
        );

        appendORIRData(
          chart.itemsByConsequence,
          consequenceORDatum,
          consequenceORDataKeys,
          consequenceIRDatum,
          consequenceIRDataKeys,
        );

        appendORIRData(
          chart.itemsByOutcomeScenario,
          outcomeORDatum,
          outcomeORDataKeys,
          outcomeIRDatum,
          outcomeIRDataKeys,
        );

        appendSystemOverviewSubgroups(
          chart.itemsByConsequence,
          consequenceORDatum,
          consequenceIRDatum,
          "By Category",
          riskUnitFormatter,
        );

        appendSystemOverviewSubgroups(
          chart.itemsByOutcomeScenario,
          outcomeORDatum,
          outcomeIRDatum,
          "Release Outcome",
          riskUnitFormatter,
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          threatORDatum,
          threatIRDatum,
          riskUnitFormatter,
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          consequenceORDatum,
          consequenceIRDatum,
          riskUnitFormatter,
        );

        appendSystemOverviewTooltipExtras(
          chart.operationalTotal ?? 0,
          chart.interventionTotal ?? 0,
          outcomeORDatum,
          outcomeIRDatum,
          riskUnitFormatter,
        );

        threatIRTab.push(threatORDatum, threatIRDatum);

        consequenceIRTab.push(consequenceORDatum, consequenceIRDatum);

        outcomeIRTab.push(outcomeORDatum, outcomeIRDatum);

        totals.push({
          band: xBand,
          total: chart.total ?? 0,
        });
      }

      // OR charts are not grouped; filter out IR series
      makeORTabData(threatIRTab, threatORTab);
      makeORTabData(consequenceIRTab, consequenceORTab);
      makeORTabData(outcomeIRTab, outcomeORTab);

      yMaxStackVal = Math.max(...totals.map((annotation) => annotation.total));
    }

    return {
      threatORTab,
      threatORDataKeys,
      threatIRTab,
      threatIRDataKeys,
      consequenceORTab,
      consequenceORDataKeys,
      consequenceIRTab,
      consequenceIRDataKeys,
      outcomeORTab,
      outcomeORDataKeys,
      outcomeIRTab,
      outcomeIRDataKeys,
      totals,
      yMaxStackVal,
    };
  }, [result.data, riskUnitFormatter]);

  return {
    ...result,
    ...series,
  };
};
